import posthog from "posthog-js";
import type { AnalyticsProperties } from "../types";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
const SESSION_REPLAY_ENABLED = process.env.REACT_APP_POSTHOG_SESSION_REPLAY === "true";

let initialized = false;

function ensureInitialized(): void {
    if (!POSTHOG_KEY || initialized) return;
    initialized = true;

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        // Loaded eagerly but captures nothing until opt_in_capturing() is
        // called (see setConsent below) - avoids re-initializing the SDK on
        // every consent change.
        opt_out_capturing_by_default: true,
        // Without this, an opted-out SDK still writes its distinct-id cookie
        // (scoped to .teqseq.com) and localStorage identifiers on first load,
        // before the visitor has answered the consent banner (observed with
        // a real browser against production). With it, persistence stays off
        // while opted out and only starts once opt_in_capturing() runs.
        // Runtime-supported (posthog-js checks it in its persistence gate) but
        // missing from posthog-js's PostHogConfig typings, hence the spread.
        ...({
            opt_out_persistence_by_default: true,
            // The SDK otherwise fetches its remote config and POSTs a
            // feature-flag lookup (project token + a random anonymous id, from
            // the visitor's IP) on every load, opted out or not - i.e. before
            // any consent (observed in a real browser; this option removes all
            // of it). Nothing here uses feature flags or surveys. Session
            // replay does read its settings from that remote config, so the
            // lookup is only kept if replay is switched on.
            advanced_disable_flags: !SESSION_REPLAY_ENABLED,
        } as Record<string, unknown>),
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // Cost control: no person profile (and no PostHog billing "person")
        // is created for purely anonymous events, only once a user is
        // identify()'d.
        person_profiles: "identified_only",
        disable_session_recording: !SESSION_REPLAY_ENABLED,
        session_recording: SESSION_REPLAY_ENABLED
            ? {
                  maskAllInputs: true,
                  maskTextSelector: "*",
              }
            : undefined,
    });
}

export function setConsent(granted: boolean): void {
    if (!POSTHOG_KEY) return;
    ensureInitialized();
    if (granted) {
        posthog.opt_in_capturing();
    } else {
        posthog.opt_out_capturing();
    }
}

export function pageview(path: string): void {
    if (!POSTHOG_KEY) return;
    posthog.capture("$pageview", { $current_url: path });
}

export function event(name: string, properties: AnalyticsProperties): void {
    if (!POSTHOG_KEY) return;
    posthog.capture(name, properties);
}

export function identify(userId: string): void {
    if (!POSTHOG_KEY) return;
    posthog.identify(userId);
}

export function reset(): void {
    if (!POSTHOG_KEY) return;
    posthog.reset();
}
